import * as React from 'react';

import InlineMarkdown from './InlineMarkdown';

interface IPrerequisiteItem {
  text: string;
  prNumbers: number[];
}

interface IPreviewCommit {
  prNumber?: number | null;
  prUrl?: string | null;
}

interface IPrerequisitesChecklistProps {
  items: IPrerequisiteItem[];
  /**
   * Commits in the current preview. Used to resolve `prNumbers` on each
   * prerequisite to the corresponding `prUrl` so the operator can click
   * through to the source PR for context. Falls back to a non-clickable
   * `#NNN` chip when a referenced PR isn't in the preview's commit list
   * (e.g. the LLM hallucinated, or the PR landed in an earlier release).
   */
  commits: IPreviewCommit[];
  emptyCopy: string;
}

/**
 * Renders the LLM-extracted prerequisites as a (read-only) checkbox list
 * with PR cross-references. The operator runs these steps externally
 * before publishing.
 *
 * **Limitations:** the checkboxes are display-only — clicks don't
 * persist state. Tracking interactive checking is out of scope for now.
 */
const PrerequisitesChecklist = ({ items, commits, emptyCopy }: IPrerequisitesChecklistProps) => {
  if (items.length === 0) {
    return <p className="text-fg-tertiary text-xs italic">{emptyCopy}</p>;
  }
  const prUrlByNumber = new Map<number, string>();
  for (const c of commits) {
    if (c.prNumber != null && c.prUrl != null) {
      prUrlByNumber.set(c.prNumber, c.prUrl);
    }
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-2 text-sm">
          <span className="border-border-secondary mt-1 inline-block h-3.5 w-3.5 shrink-0 border" aria-hidden="true" />
          <span className="text-fg-primary">
            <InlineMarkdown text={item.text} />
            {item.prNumbers.length > 0 && (
              <span className="text-fg-tertiary ml-1 text-xs">
                {'('}
                {item.prNumbers.map((n, i) => {
                  const url = prUrlByNumber.get(n);
                  return (
                    <React.Fragment key={n}>
                      {i > 0 && ', '}
                      {url ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-fg-brand-primary hover:text-fg-brand-primary_alt underline underline-offset-2"
                        >
                          #{n}
                        </a>
                      ) : (
                        <span className="font-mono">#{n}</span>
                      )}
                    </React.Fragment>
                  );
                })}
                {')'}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
};

export default PrerequisitesChecklist;
